import logger from './logger';

interface SendNotificationParams {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  data?: any;
  targetRoles?: string[];
  targetUserId?: string;
}

const API_URL = '/api';

export const sendPushNotification = async (params: SendNotificationParams) => {
  try {
    const response = await fetch(`${API_URL}/rpc/send_push_notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const error = await response.text();
      logger.error('Error sending push notification:', error);
      return { success: false, error };
    }

    const data = await response.json();
    logger.log('Push notification sent:', data);
    return { success: true, data };
  } catch (error) {
    logger.error('Failed to send push notification:', error);
    return { success: false, error };
  }
};

// Helper function to notify supervisors about process completion
export const notifyProcessCompleted = async (processName: string, operatorName: string, workOrderNumber: string) => {
  return sendPushNotification({
    title: '✅ Proceso Completado',
    body: `${operatorName} completó ${processName} en orden ${workOrderNumber}`,
    data: {
      type: 'process_completed',
      workOrderNumber,
    },
    targetRoles: ['supervisor', 'admin'],
  });
};

// Helper function to notify about process issues
export const notifyProcessIssue = async (processName: string, issue: string, workOrderNumber: string) => {
  return sendPushNotification({
    title: '⚠️ Problema en Proceso',
    body: `Problema en ${processName} (Orden ${workOrderNumber}): ${issue}`,
    data: {
      type: 'process_issue',
      workOrderNumber,
      issue,
    },
    targetRoles: ['supervisor', 'admin'],
  });
};

// Helper function to notify about work order status changes
export const notifyWorkOrderUpdate = async (workOrderNumber: string, status: string) => {
  return sendPushNotification({
    title: '📋 Actualización de Orden',
    body: `Orden ${workOrderNumber} cambió a: ${status}`,
    data: {
      type: 'work_order_update',
      workOrderNumber,
      status,
    },
    targetRoles: ['supervisor', 'admin'],
  });
};

// Helper function to notify vacuum process completion
export const notifyVacuumCompleted = async (operatorName: string, workOrderNumber: string, pressure?: string) => {
  const pressureInfo = pressure ? ` (Presión: ${pressure})` : '';
  return sendPushNotification({
    title: '🔵 Vacío Completado',
    body: `${operatorName} completó el vacío en orden ${workOrderNumber}${pressureInfo}. Listo para curado.`,
    data: {
      type: 'vacuum_completed',
      workOrderNumber,
      pressure,
    },
    targetRoles: ['supervisor', 'admin', 'operator'],
  });
};

// Helper function to notify vacuum overtime
export const notifyVacuumOvertime = async (workOrderNumber: string, elapsedMinutes: number, estimatedMinutes: number) => {
  return sendPushNotification({
    title: '⏰ Vacío Excedido',
    body: `El vacío de orden ${workOrderNumber} lleva ${elapsedMinutes} min (estimado: ${estimatedMinutes} min)`,
    data: {
      type: 'vacuum_overtime',
      workOrderNumber,
      elapsedMinutes,
      estimatedMinutes,
    },
    targetRoles: ['supervisor', 'admin'],
  });
};

// Helper function to notify freezer out-time warning
export const notifyFreezerOutTimeWarning = async (
  packageCode: string,
  workOrderNumber: string,
  minutesElapsed: number,
  maxOutTimeMinutes: number
) => {
  const minutesRemaining = maxOutTimeMinutes - minutesElapsed;
  const emoji = minutesRemaining <= 0 ? '🔴' : minutesRemaining <= 10 ? '⚠️' : '⏰';
  const severity = minutesRemaining <= 0 ? 'critical' : minutesRemaining <= 10 ? 'warning' : 'info';

  return sendPushNotification({
    title: `${emoji} Alerta Out-Time Congelador`,
    body: minutesRemaining <= 0
      ? `EXCEDIDO: ${packageCode} (OT ${workOrderNumber}) lleva ${minutesElapsed} min fuera del congelador`
      : `${packageCode} (OT ${workOrderNumber}) - ${minutesRemaining} min restantes`,
    data: {
      type: 'freezer_out_time_warning',
      packageCode,
      workOrderNumber,
      minutesElapsed,
      maxOutTimeMinutes,
      severity,
    },
    targetRoles: ['supervisor', 'admin'],
  });
};
